import * as React from 'react';
import { FilterEditProps, FilterProviderBase, isGenericEmptyValue } from '.';
import { Filter } from '..';
import { BooleanFilterEdit, SingleDecimalFilterEdit, SingleIntegerFilterEdit, SingleTextFilterEdit } from '../editors';
import SingleDropDownFilterEdit from '../editors/SingleDropDownFilterEdit';

export interface StringFilterEditProps extends FilterEditProps<string> {}
export interface NumberFilterEditProps extends FilterEditProps<number> {}

/**
 * Default provider for single values, generally just strings as this works nicely with how React
 * treats values in input tags, as well as how it will be sent to the server.
 *
 * @export
 * @abstract
 * @class SingleValueFilterProvider
 * @implements {FilterProviderBase<string>}
 */
export abstract class SingleValueFilterProvider implements FilterProviderBase<string> {
	public abstract getComponent(): any;
	// eslint-disable-next-line no-unused-vars
	public getComponentProps = (filter?: Filter) => ({});
	public getFriendlyValueForDisplay = (value: string): React.ReactNode =>
		value === '' ? <em>&lt;Empty&gt;</em> : value;
	public getValueFromQueryString = (query: string) => query;
	public getQueryStringFromValue = (value: string) => value;
	public isEmptyValue = (value: string) => isGenericEmptyValue(value);
}

/**
 * Filter provider for single text values. Inherits the default implementation and only specifies a custom component.
 *
 * @export
 * @class SingleValueFilterProvider
 * @extends {SingleValueFilterProvider}
 */
export class SingleTextFilterProvider extends SingleValueFilterProvider {
	public getComponent = (): any => SingleTextFilterEdit;
}

/**
 * Filter provider for single integer values. Inherits the default implementation and only specifies a custom component.
 *
 * @export
 * @class SingleIntegerFilterProvider
 * @extends {SingleValueFilterProvider}
 */
export class SingleIntegerFilterProvider extends SingleValueFilterProvider {
	public getComponent = (): any => SingleIntegerFilterEdit;
}

/**
 * Filter provider for single decimal values. Inherits the default implementation and only specifies a custom component.
 *
 * @export
 * @class SingleDecimalFilterProvider
 * @extends {SingleValueFilterProvider}
 */
export class SingleDecimalFilterProvider extends SingleValueFilterProvider {
	public getComponent = (): any => SingleDecimalFilterEdit;
}

/**
 * Filter provider for single boolean values. Inherits the default implementation and specifies a custom component a
 * and overrides the friendly text displayed in the list of selected values.
 *
 * @export
 * @class SingleBooleanFilterProvider
 * @extends {SingleValueFilterProvider}
 */
export class SingleBooleanFilterProvider extends SingleValueFilterProvider {
	public getComponent = (): any => BooleanFilterEdit;
	public getFriendlyValueForDisplay = (value: string): React.ReactNode =>
		value === 'true' ? 'Yes' : value === 'false' ? 'No' : '<Empty>';
	public getValueFromQueryString = (query: string) => {
		if (query !== 'true' && query !== 'false' && query !== '') {
			throw new Error('Unable to parse boolean from query string.');
		}
		return query;
	};
}

/**
 * Provider for single value selected via a dropdown
 *
 * @export
 * @class SingleDropdownFilterProvider
 * @extends {SingleValueFilterProvider}
 */
export class SingleDropdownFilterProvider extends SingleValueFilterProvider {
	public getComponent = () => SingleDropDownFilterEdit;
	public getComponentProps = (filter: Filter) =>
		(filter.options && { dropdownValues: filter.options.dropdownValues }) || {};
}
